<template>
  <span class="document-attachment">
    <template v-if="url">
      <button
        v-if="isImage && !imageError"
        type="button"
        class="document-attachment__thumbnail"
        :aria-label="'Preview ' + label"
        @click="showPreview = true"
      >
        <img :src="url" :alt="label" @error="imageError = true" />
      </button>
      <v-btn
        v-else
        small
        text
        color="primary"
        :href="url"
        :download="filename"
        target="_blank"
        rel="noopener noreferrer"
        :aria-label="'Download ' + label"
      >
        <v-icon left small>{{ fileIcon }}</v-icon>
        Download {{ extension ? extension.toUpperCase() : 'file' }}
      </v-btn>

      <v-dialog v-if="isImage" v-model="showPreview" max-width="1000">
        <v-card v-if="showPreview">
          <v-toolbar flat dense>
            <v-toolbar-title>{{ label }}</v-toolbar-title>
            <v-spacer />
            <v-btn
              icon
              :href="url"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="'Open ' + label + ' in a new tab'"
              title="Open in a new tab"
            >
              <v-icon>mdi-open-in-new</v-icon>
            </v-btn>
            <v-btn icon aria-label="Close preview" @click="showPreview = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <img
              v-if="!imageError"
              class="document-attachment__image"
              :src="url"
              :alt="label"
              @error="imageError = true"
            />
            <p v-else>Preview unavailable.</p>
            <p class="mt-3 mb-0">
              <a :href="url" target="_blank" rel="noopener noreferrer">
                Open or download {{ label }}
              </a>
              if the preview is unavailable.
            </p>
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
    <span v-else class="text--secondary">—</span>
  </span>
</template>

<script>
export default {
  name: "DocumentAttachment",
  props: {
    url: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "Document",
    },
  },
  data() {
    return {
      showPreview: false,
      imageError: false,
    };
  },
  computed: {
    filename() {
      // Inspect only the filename; leave signed query parameters untouched.
      const filename = (this.url || "").split(/[?#]/)[0].split("/").pop();
      let decodedFilename = filename;
      try {
        decodedFilename = decodeURIComponent(filename);
      } catch (error) {
        // A malformed escape must not prevent the document from being opened.
      }
      return decodedFilename || this.label;
    },
    extension() {
      const match = this.filename.match(/\.([a-z0-9]+)$/i);
      return match ? match[1].toLowerCase() : "";
    },
    isImage() {
      return [
        "jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif",
        "ico", "tif", "tiff", "heic", "heif",
      ].includes(this.extension);
    },
    fileIcon() {
      if (this.extension === "pdf") return "mdi-file-pdf-box";
      return ["doc", "docx"].includes(this.extension)
        ? "mdi-file-word-box"
        : "mdi-file-document-outline";
    },
  },
  watch: {
    url() {
      this.showPreview = false;
      this.imageError = false;
    },
  },
};
</script>

<style scoped>
.document-attachment__thumbnail {
  display: inline-flex;
  margin-left: 4px;
  cursor: pointer;
  vertical-align: middle;
}

.document-attachment__thumbnail img {
  width: 50px;
  height: 40px;
  object-fit: contain;
}

.document-attachment__image {
  display: block;
  max-width: 100%;
  max-height: 65vh;
  margin: 0 auto;
  object-fit: contain;
}
</style>
